<div class="users-table-container">
  <div class="table-header">
    <h2 class="section-title">Users</h2>
  </div>
  <p-table
    [scrollable]="true"
    [value]="filteredUsers()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-user>
      <tr>
        <td>
          <strong>{{ user.name }}</strong>
        </td>
        <td>
          <span class="secondary-text">{{ user.email }}</span>
        </td>
        <td>
          <span class="secondary-text">{{ user.externalId || 'N/A' }}</span>
        </td>
        <td>
          @if (user.groups.length > 0) {
            <a
              pTooltip="View groups and roles on the user detail"
              [routerLink]="['/access-management/users', user.userId]"
            >
              <p-tag
                severity="info"
                [value]="user.groups.length + ' group' + (user.groups.length !== 1 ? 's' : '')"
                [style]="{ cursor: 'pointer' }"
              ></p-tag>
            </a>
          } @else {
            <span class="secondary-text">No groups</span>
          }
        </td>
        <td>
          <p-tag [value]="getStatusLabel(user.isActive)" [severity]="getStatusSeverity(user.isActive)"></p-tag>
        </td>
        <td>{{ formatDate(user.createdOn) }}</td>
        <td>
          <div class="action-buttons">
            <p-button
              icon="pi pi-eye"
              severity="info"
              pTooltip="View profile"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [routerLink]="['/access-management/users', user.userId]"
            ></p-button>
            <p-button
              icon="pi pi-box"
              severity="success"
              pTooltip="Manage Packages"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onViewMemberships(user)"
            ></p-button>
            <p-button
              icon="pi pi-trash"
              severity="danger"
              pTooltip="Delete"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [loading]="deletingId() === user.userId"
              (onClick)="onDeleteUser(user)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            <i class="pi pi-user large-icon"></i>
            <p>No users found</p>
            @if (searchTerm()) {
              <small>Try adjusting your search criteria</small>
            } @else {
              <small>Create your first user to get started</small>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- Create User Dialog -->
<p-dialog
  header="Create User"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDialog"
>
  <div class="dialog-content">
    <div class="form-field">
      <label for="externalId">External ID *</label>
      <input
        class="field-input"
        id="externalId"
        type="text"
        pInputText
        placeholder="Enter external user ID (e.g., from identity provider)"
        [formField]="userForm.externalId"
      />
      @let externalIdField = userForm.externalId();
      @if (externalIdField.touched() && !externalIdField.valid()) {
        <small class="p-error">
          @for (error of externalIdField.errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="email">Email *</label>
      <input
        class="field-input"
        id="email"
        type="email"
        pInputText
        placeholder="Enter user email"
        [formField]="userForm.email"
      />
      @let emailField = userForm.email();
      @if (emailField.touched() && !emailField.valid()) {
        <small class="p-error">
          @for (error of emailField.errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="name">Name *</label>
      <input
        class="field-input"
        id="name"
        type="text"
        pInputText
        placeholder="Enter user full name"
        [formField]="userForm.name"
      />
      @let nameField = userForm.name();
      @if (nameField.touched() && !nameField.valid()) {
        <small class="p-error">
          @for (error of nameField.errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="userForm.isActive" />
        <span>Active (user can access the system)</span>
      </label>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="showDialog.set(false)"></p-button>
    <p-button label="Create" [loading]="saving()" [disabled]="!isFormValid()" (onClick)="onSaveUser()"></p-button>
  </ng-template>
</p-dialog>

<!-- Manage Memberships Dialog -->
<p-dialog
  [header]="'Manage Packages for ' + (selectedUserForMemberships()?.name || '')"
  [modal]="true"
  [style]="{ width: '70vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showMembershipsDialog"
>
  <div class="memberships-management">
    <div class="form-field">
      <label for="accountSelect">Payment Account *</label>
      <p-select
        id="accountSelect"
        optionLabel="name"
        optionValue="accountId"
        placeholder="Select payment account"
        appendTo="body"
        [options]="accounts()"
        [filter]="true"
        [style]="{ width: '100%' }"
        [(ngModel)]="selectedAccountId"
        (onChange)="onAccountChange(selectedAccountId())"
      >
        <ng-template let-account pTemplate="item">
          <div class="dropdown-item">
            <strong>{{ account.name }}</strong>
            @if (account.description) {
              <small class="secondary-text">{{ account.description }}</small>
            }
          </div>
        </ng-template>
      </p-select>
    </div>

    @if (selectedAccountId()) {
      @if (membershipsLoading()) {
        <div class="loading-state">
          <p-progressSpinner [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>
      } @else if (currentUserMemberships(); as memberships) {
        <div class="memberships-section">
          <h4>Active Memberships ({{ memberships.activeMembershipCount }})</h4>
          @if (memberships.memberships.length > 0) {
            <div class="memberships-list">
              @for (membership of memberships.memberships; track membership.membershipId) {
                <div class="membership-item" [class.inactive]="!membership.isActive">
                  <div class="membership-info">
                    <div class="package-details">
                      <strong>{{ membership.packageName }}</strong>
                      <small class="secondary-text">Option: {{ membership.optionName }}</small>
                    </div>
                    <div class="status-column">
                      <p-tag
                        [value]="membership.isActive ? 'Active' : 'Cancelled'"
                        [severity]="membership.isActive ? 'success' : 'danger'"
                      ></p-tag>
                      <div class="membership-meta">
                        <small class="secondary-text">Assigned: {{ formatDate(membership.assignedOn) }}</small>
                        @if (membership.cancelledOn) {
                          <small class="text-danger">Cancelled: {{ formatDate(membership.cancelledOn) }}</small>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-small">
              <i class="pi pi-box"></i>
              <p>No packages allocated to this user yet</p>
            </div>
          }
        </div>
      }
    } @else {
      <div class="empty-state-small">
        <i class="pi pi-info-circle"></i>
        <p>Please select an account to view and manage memberships</p>
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Close" severity="secondary" (onClick)="showMembershipsDialog.set(false)"></p-button>
  </ng-template>
</p-dialog>

<app-delete-entity-dialog
  entityKind="user"
  [entityName]="userPendingDelete()?.name ?? ''"
  [submitting]="deletingId() === userPendingDelete()?.userId"
  [(visible)]="showDeleteDialog"
  (confirm)="onConfirmDelete($event)"
></app-delete-entity-dialog>
